/**
 * The one preference the service worker also has to know.
 *
 * The app keeps its preferences in `localStorage` — synchronous, so the choice
 * is applied before the first paint and nothing flashes. A service worker has
 * no `localStorage` at all, and a `push` arrives precisely when no page is open
 * to ask, so `clients.matchAll()` is no help either.
 *
 * The Cache API is the one store both sides can reach. So the window writes a
 * one-line mirror here whenever the choice changes, and the worker reads it
 * when it has to name a notification. `localStorage` stays the source of truth;
 * this is a copy for a reader that cannot see the original.
 */

const CACHE = "vmeste.prefs";
const LOCALE_URL = "/__prefs/locale";

/** Fire and forget: a notification in the wrong language beats a crash on boot. */
export function mirrorLocale(locale: string): void {
  if (typeof caches === "undefined") return;

  void caches
    .open(CACHE)
    .then((cache) => cache.put(LOCALE_URL, new Response(locale)))
    .catch(() => {
      // Private browsing, storage denied, quota. Nothing here is worth a throw.
    });
}

/** Read the mirror. `null` when it was never written — the worker then guesses. */
export async function readMirroredLocale(): Promise<string | null> {
  if (typeof caches === "undefined") return null;

  try {
    const cache = await caches.open(CACHE);
    const hit = await cache.match(LOCALE_URL);
    return hit ? (await hit.text()).trim() || null : null;
  } catch {
    return null;
  }
}
